import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "preview", "embedCode"]
  static values = { 
    instagramPattern: String,
    tiktokPattern: String,
    youtubePattern: String 
  }

  connect() {
    // Set default patterns if not provided
    this.instagramPatternValue = this.instagramPatternValue || "https://www.instagram.com/p/([A-Za-z0-9_-]+)"
    this.tiktokPatternValue = this.tiktokPatternValue || "https://www.tiktok.com/@([^/]+)/video/([0-9]+)"
    this.youtubePatternValue = this.youtubePatternValue || "(?:youtube\\.com/watch\\?v=|youtu\\.be/)([A-Za-z0-9_-]+)"
  }

  processUrl() {
    const url = this.inputTarget.value.trim()
    
    if (!url) {
      this.clearPreview()
      return
    }

    // Detect platform and generate embed
    if (this.isInstagramUrl(url)) {
      this.generateInstagramEmbed(url)
    } else if (this.isTikTokUrl(url)) {
      this.generateTikTokEmbed(url)
    } else if (this.isYouTubeUrl(url)) {
      this.generateYouTubeEmbed(url)
    } else {
      this.showUnsupportedUrlMessage()
    }
  }

  isInstagramUrl(url) {
    return /instagram\.com\/p\/[A-Za-z0-9_-]+/.test(url)
  }

  isTikTokUrl(url) {
    return /tiktok\.com\/@[^/]+\/video\/[0-9]+/.test(url)
  }

  isYouTubeUrl(url) {
    return /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]+)/.test(url)
  }

  generateInstagramEmbed(url) {
    // Extract post ID
    const match = url.match(/instagram\.com\/p\/([A-Za-z0-9_-]+)/)
    if (!match) return

    const postId = match[1]
    const embedUrl = `https://www.instagram.com/p/${postId}/embed`
    
    const embedCode = `<iframe src="${embedUrl}" width="400" height="480" frameborder="0" scrolling="no" allowtransparency="true" class="instagram-embed"></iframe>`
    
    this.showPreview(embedCode, 'Instagram Post')
    this.setEmbedCode(embedCode)
  }

  generateTikTokEmbed(url) {
    // TikTok embeds require their official embed code
    // For now, we'll create a link preview
    const embedCode = `<div class="tiktok-embed-placeholder">
      <a href="${url}" target="_blank" rel="noopener noreferrer" class="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
        <div class="flex-shrink-0">
          <i class="fab fa-tiktok text-2xl text-black"></i>
        </div>
        <div>
          <p class="font-medium text-gray-900">View on TikTok</p>
          <p class="text-sm text-gray-500">${url}</p>
        </div>
      </a>
    </div>`
    
    this.showPreview(embedCode, 'TikTok Video')
    this.setEmbedCode(embedCode)
  }

  generateYouTubeEmbed(url) {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]+)/)
    if (!match) return

    const videoId = match[1]
    const embedCode = `<div class="aspect-w-16 aspect-h-9">
      <iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen class="w-full h-full"></iframe>
    </div>`
    
    this.showPreview(embedCode, 'YouTube Video')
    this.setEmbedCode(embedCode)
  }

  showPreview(embedCode, type) {
    if (!this.hasPreviewTarget) return

    this.previewTarget.innerHTML = `
      <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <h4 class="text-sm font-medium text-gray-900 mb-2">${type} Preview:</h4>
        <div class="bg-white rounded border">
          ${embedCode}
        </div>
      </div>
    `
  }

  setEmbedCode(code) {
    if (this.hasEmbedCodeTarget) {
      this.embedCodeTarget.value = code
    }
  }

  showUnsupportedUrlMessage() {
    if (!this.hasPreviewTarget) return

    this.previewTarget.innerHTML = `
      <div class="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
        <div class="flex items-center">
          <i class="ph-fill ph-warning text-yellow-600 mr-2"></i>
          <p class="text-sm text-yellow-800">
            URL not supported. Currently supports Instagram, TikTok, and YouTube links.
          </p>
        </div>
      </div>
    `
  }

  clearPreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ''
    }
    if (this.hasEmbedCodeTarget) {
      this.embedCodeTarget.value = ''
    }
  }

  copyEmbedCode() {
    if (!this.hasEmbedCodeTarget) return

    const embedCode = this.embedCodeTarget.value
    if (!embedCode) return

    if (navigator.clipboard) {
      navigator.clipboard.writeText(embedCode).then(() => {
        this.showCopiedMessage('Embed code copied!')
      })
    } else {
      // Fallback
      this.embedCodeTarget.select()
      document.execCommand('copy')
      this.showCopiedMessage('Embed code copied!')
    }
  }

  showCopiedMessage(message) {
    const notification = document.createElement('div')
    notification.textContent = message
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50'
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      document.body.removeChild(notification)
    }, 2000)
  }
}